import { useState, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from "recharts";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

const BASE = "/metrics-api";

const SERVERS = {
  dev:      { label:"TN-Dev",           accent:"#06b6d4", env:"tnpdcl" },
  prod:     { label:"TN-PS-One",        accent:"#f97316", env:"tnpdcl" },
  device:   { label:"TN-PS-MQTT/TCP",   accent:"#8b5cf6", env:"tnpdcl" },
  micro:    { label:"TN-Micro",         accent:"#10b981", env:"tnpdcl" },
  ps3:      { label:"TN-PS-Three",      accent:"#e11d48", env:"tnpdcl" },
  ep:       { label:"TN-Config/Solar",  accent:"#f59e0b", env:"tnpdcl" },
  reproc:   { label:"TAN-Events/Agg",   accent:"#a78bfa", env:"tnpdcl" },
  dataproc: { label:"TN-Data-Proc",     accent:"#06b6d4", env:"tnpdcl" },
  db:       { label:"DB-Primary",       accent:"#10b981", env:"tnpdcl" },
  dbsec1:   { label:"DB-Secondary-1",   accent:"#22c55e", env:"tnpdcl" },
  dbsec2:   { label:"DB-Secondary-2",   accent:"#16a34a", env:"tnpdcl" },
  fsascript:  { label:"FSA-Script",     accent:"#14b8a6", env:"fsa" },
  fsadocker1: { label:"FSA-Docker-1",   accent:"#0d9488", env:"fsa" },
  fsarabbitmq:{ label:"FSA-RabbitMQ",   accent:"#f59e0b", env:"fsa" },
  fsakafkazk: { label:"FSA-Kafka-ZK",   accent:"#8b5cf6", env:"fsa" },
  fsaevents:  { label:"FSA-Events",     accent:"#06b6d4", env:"fsa" },
  fsaconfig:  { label:"FSA-Config",     accent:"#f97316", env:"fsa" },
  fsagps:     { label:"FSA-GPS",        accent:"#e11d48", env:"fsa" },
  fsadb:      { label:"FSA-DB-1",       accent:"#10b981", env:"fsa" },
  fsadb2:     { label:"FSA-DB-2",       accent:"#22c55e", env:"fsa" },
  fsadb3:     { label:"FSA-DB-3",       accent:"#16a34a", env:"fsa" },
};

const RANGES = [
  { label:"1H",  hours:1  },
  { label:"6H",  hours:6  },
  { label:"12H", hours:12 },
  { label:"24H", hours:24 },
  { label:"3D",  hours:72 },
  { label:"7D",  hours:168},
];

function fmt(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:false});
}
function fmtDate(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString("en-IN",{day:"2-digit",month:"short"}) + " " +
    d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:false});
}

const CustomTooltip = ({ active, payload, label, theme }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{background:theme.bgCard,border:`1px solid ${theme.border}`,
      borderRadius:10,padding:"10px 14px",fontFamily:"'Inter',sans-serif",
      boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
      <div style={{fontSize:11,color:theme.textMuted,marginBottom:6}}>{fmtDate(label)}</div>
      {payload.map(p=>(
        <div key={p.name} style={{fontSize:12,color:p.color,fontWeight:600,marginBottom:2}}>
          {p.name}: <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{typeof p.value==="number"?p.value.toFixed(1):p.value}{p.unit||"%"}</span>
        </div>
      ))}
    </div>
  );
};

export default function HistoryPanel({ token, theme, env }) {
  const curEnv = env || sessionStorage.getItem("bn360_env") || "tnpdcl";
  const visibleServers = Object.fromEntries(
    Object.entries(SERVERS).filter(([k,v]) => (v.env||"tnpdcl")===curEnv)
  );
  const [server,   setServer]   = useState("prod");
  const [range,    setRange]    = useState(24);
  const [useCustom, setUseCustom] = useState(false);
  const [fromDt,   setFromDt]   = useState("");
  const [toDt,     setToDt]     = useState("");
  const [tab,      setTab]      = useState("server"); // server | service
  const [chartType, setChartType] = useState("area"); // area | bar
  const [svrData,  setSvrData]  = useState([]);
  const [svcList,  setSvcList]  = useState([]);
  const [selSvc,   setSelSvc]   = useState("");
  const [svcData,  setSvcData]  = useState([]);
  const [loading,  setLoading]  = useState(false);

  const headers = { "X-Auth-Token": token };

  const loadServerData = useCallback(async () => {
    setLoading(true);
    try {
      const params = useCustom && fromDt && toDt
        ? `server=${server}&from=${encodeURIComponent(fromDt)}&to=${encodeURIComponent(toDt)}`
        : `server=${server}&hours=${range}`;
      const r = await fetch(`${BASE}/history/servers?${params}`, {headers});
      const d = await r.json();
      setSvrData(d);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [server, range, token]);

  const loadSvcList = useCallback(async () => {
    try {
      const params2 = useCustom && fromDt && toDt
        ? `server=${server}&from=${encodeURIComponent(fromDt)}&to=${encodeURIComponent(toDt)}`
        : `server=${server}&hours=${range}`;
      const r = await fetch(`${BASE}/history/services?${params2}`, {headers});
      const d = await r.json();
      const list = d.map(x=>x.service).filter(Boolean).sort();
      setSvcList(list);
      if (list.length && !list.includes(selSvc)) setSelSvc(list[0]);
    } catch(e) { console.error(e); }
  }, [server, range, token]);

  const loadSvcData = useCallback(async () => {
    if (!selSvc) return;
    setLoading(true);
    try {
      const params3 = useCustom && fromDt && toDt
        ? `server=${server}&service=${encodeURIComponent(selSvc)}&from=${encodeURIComponent(fromDt)}&to=${encodeURIComponent(toDt)}`
        : `server=${server}&service=${encodeURIComponent(selSvc)}&hours=${range}`;
      const r = await fetch(`${BASE}/history/services?${params3}`, {headers});
      const d = await r.json();
      setSvcData(d);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [server, selSvc, range, token]);

  useEffect(() => { loadServerData(); loadSvcList(); }, [server, range, useCustom, fromDt, toDt]);
  useEffect(() => { if (tab==="service") loadSvcData(); }, [tab, selSvc, server, range, useCustom, fromDt, toDt]);

  const cfg = SERVERS[server] || Object.values(visibleServers)[0] || Object.values(SERVERS)[0];
  const isDark = theme.bg === "#0d1117";

  const gridColor = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)";
  const axisColor = isDark ? "#475569" : "#94a3b8";
  const smartFmt = (ts) => (range > 12 || useCustom) ? fmtDate(ts) : fmt(ts);

  const downloadPDF = async () => {
    try {
      const doc = new jsPDF();
      const srvLabel = (visibleServers[server]?.label) || server;
      const rangeLabel = useCustom && fromDt && toDt ? (fromDt + " to " + toDt) : ("Last " + range + "h");
      const now = new Date().toLocaleString();
      doc.setFontSize(16); doc.setTextColor(40);
      doc.text("BN360 History Report", 14, 18);
      doc.setFontSize(10); doc.setTextColor(100);
      doc.text("Server: " + srvLabel, 14, 26);
      doc.text("Range: " + rangeLabel, 14, 32);
      doc.text("View: " + (tab === "server" ? "Server Metrics" : "Service: " + selSvc), 14, 38);
      doc.text("Generated: " + now, 14, 44);
      let tableStartY = 50;
      try {
        const chartEl = document.getElementById(tab === "server" ? "bn360-charts-server" : "bn360-charts-service");
        if (chartEl) {
          const canvas = await html2canvas(chartEl, { scale: 1.2, backgroundColor: "#ffffff", useCORS: true });
          const imgData = canvas.toDataURL("image/png");
          const pageW = doc.internal.pageSize.getWidth() - 28;
          const imgH = (canvas.height * pageW) / canvas.width;
          let drawH = imgH; let drawW = pageW;
          const maxH = doc.internal.pageSize.getHeight() - 60;
          if (imgH > maxH) { drawH = maxH; drawW = (canvas.width * maxH) / canvas.height; }
          doc.addImage(imgData, "PNG", 14, 50, drawW, drawH);
          doc.addPage();
          tableStartY = 20;
        }
      } catch (ce) {}
      const fmtTs = (t) => { try { return new Date(t).toLocaleString(); } catch (e) { return t; } };
      if (tab === "server") {
        const rows = (svrData || []).map((d) => [
          fmtTs(d.ts),
          d.cpu != null ? d.cpu + "%" : "-",
          d.ram != null ? d.ram + "%" : "-",
          d.disk_root != null ? d.disk_root + "%" : "-",
        ]);
        autoTable(doc, { startY: tableStartY, head: [["Timestamp", "CPU %", "RAM %", "Disk %"]],
          body: rows, styles: { fontSize: 8 }, headStyles: { fillColor: [99, 102, 241] } });
      } else {
        const rows = (svcData || []).map((d) => [
          fmtTs(d.ts),
          d.cpu != null ? d.cpu + "%" : "-",
          d.mem != null ? d.mem + "%" : (d.ram != null ? d.ram + "%" : "-"),
          d.status || "-",
        ]);
        autoTable(doc, { startY: tableStartY, head: [["Timestamp", "CPU %", "MEM %", "Status"]],
          body: rows, styles: { fontSize: 8 }, headStyles: { fillColor: [99, 102, 241] } });
      }
      const safe = (s) => String(s).replace(/[^a-z0-9]/gi, "-");
      doc.save("bn360-history-" + safe(srvLabel) + "-" + safe(rangeLabel) + ".pdf");
    } catch (err) {
      alert("PDF generation failed: " + err.message);
    }
  };

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",
      background:theme.bg,fontFamily:"'Inter',sans-serif",minHeight:0}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Controls */}
      <div style={{padding:"12px 20px",borderBottom:`1px solid ${theme.border}`,
        background:theme.bgCard,flexShrink:0,display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>

        {/* Server selector */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {Object.entries(visibleServers).map(([k,s])=>(
            <button key={k} onClick={()=>setServer(k)} style={{
              fontSize:12,padding:"5px 12px",borderRadius:6,cursor:"pointer",
              fontFamily:"'Inter',sans-serif",fontWeight:600,
              background:server===k?`${s.accent}18`:theme.inputBg,
              border:`1px solid ${server===k?`${s.accent}50`:theme.border}`,
              color:server===k?s.accent:theme.textMuted,transition:"all .15s",
            }}>{s.label}</button>
          ))}
        </div>

        <div style={{width:1,height:20,background:theme.border}}/>

        {/* Range selector */}
        <div style={{display:"flex",gap:4}}>
          {RANGES.map(r=>(
            <button key={r.hours} onClick={()=>setRange(r.hours)} style={{
              fontSize:12,padding:"4px 10px",borderRadius:5,cursor:"pointer",
              fontFamily:"'JetBrains Mono',monospace",fontWeight:600,
              background:range===r.hours?`${cfg.accent}18`:theme.inputBg,
              border:`1px solid ${range===r.hours?`${cfg.accent}50`:theme.border}`,
              color:range===r.hours?cfg.accent:theme.textMuted,transition:"all .15s",
            }}>{r.label}</button>
          ))}
        </div>
        <div style={{width:1,height:20,background:theme.border}}/>
        <div style={{display:"flex",gap:4}}>
          {[["area","📈 Area"],["bar","📊 Bar"]].map(([t,l])=>(
            <button key={t} onClick={()=>setChartType(t)} style={{
              fontSize:12,padding:"4px 10px",borderRadius:5,cursor:"pointer",
              fontFamily:"'Inter',sans-serif",fontWeight:600,
              background:chartType===t?`${cfg.accent}18`:theme.inputBg,
              border:`1px solid ${chartType===t?`${cfg.accent}50`:theme.border}`,
              color:chartType===t?cfg.accent:theme.textMuted,transition:"all .15s",
            }}>{l}</button>
          ))}
        </div>
        <div style={{flex:1}}/>
        <button onClick={downloadPDF} title="Download PDF report" style={{
          fontSize:12,padding:"6px 14px",borderRadius:6,cursor:"pointer",
          fontFamily:"'Inter',sans-serif",fontWeight:700,
          background:"#6366f1",border:"1px solid #6366f1",color:"#fff",
          display:"flex",alignItems:"center",gap:6}}>📄 Download PDF</button>

        {/* Date range picker */}
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 20px",
          borderTop:`1px solid ${theme.border}`,flexWrap:"wrap",background:theme.bgCard}}>
          <span style={{fontSize:11,fontWeight:700,color:theme.textMuted,
            fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap",letterSpacing:".08em"}}>📅 DATE RANGE</span>
          <div style={{display:"flex",alignItems:"center",gap:4,
            background:theme.inputBg,border:`2px solid ${useCustom?cfg.accent:theme.border}`,
            borderRadius:8,padding:"0 12px",height:34,transition:"border .2s"}}>
            <span style={{fontSize:10,fontWeight:700,color:theme.textMuted,
              fontFamily:"'Inter',sans-serif",letterSpacing:".06em",marginRight:4}}>FROM</span>
            <input type="text" placeholder="YYYY-MM-DD HH:MM"
              value={fromDt.replace("T"," ")}
              onChange={e=>setFromDt(e.target.value.replace(" ","T"))}
              onFocus={e=>{
                // switch to datetime-local on focus for browser picker
                e.target.type="datetime-local";
              }}
              onBlur={e=>{ e.target.type="text"; }}
              style={{background:"transparent",border:"none",outline:"none",
                color:theme.text,fontSize:12,fontFamily:"'JetBrains Mono',monospace",
                width:160,cursor:"pointer"}}/>
          </div>
          <span style={{fontSize:16,color:theme.textMuted}}>→</span>
          <div style={{display:"flex",alignItems:"center",gap:4,
            background:theme.inputBg,border:`2px solid ${useCustom?cfg.accent:theme.border}`,
            borderRadius:8,padding:"0 12px",height:34,transition:"border .2s"}}>
            <span style={{fontSize:10,fontWeight:700,color:theme.textMuted,
              fontFamily:"'Inter',sans-serif",letterSpacing:".06em",marginRight:4}}>TO</span>
            <input type="text" placeholder="YYYY-MM-DD HH:MM"
              value={toDt.replace("T"," ")}
              onChange={e=>setToDt(e.target.value.replace(" ","T"))}
              onFocus={e=>{ e.target.type="datetime-local"; }}
              onBlur={e=>{ e.target.type="text"; }}
              style={{background:"transparent",border:"none",outline:"none",
                color:theme.text,fontSize:12,fontFamily:"'JetBrains Mono',monospace",
                width:160,cursor:"pointer"}}/>
          </div>
          <button onClick={()=>{if(fromDt&&toDt){setUseCustom(true);}else{alert("Please select From and To date/time");}}} style={{
            background:useCustom?cfg.accent:`${cfg.accent}15`,
            border:`2px solid ${cfg.accent}`,color:useCustom?"#fff":cfg.accent,
            borderRadius:8,padding:"6px 18px",cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:700,
            transition:"all .15s",
          }}>Apply</button>
          {useCustom&&(
            <button onClick={()=>{setUseCustom(false);setFromDt("");setToDt("");}} style={{
              background:"rgba(239,68,68,0.1)",border:"2px solid rgba(239,68,68,0.4)",
              color:"#ef4444",borderRadius:8,padding:"6px 14px",cursor:"pointer",
              fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
            }}>✕ Clear</button>
          )}
          {useCustom&&fromDt&&toDt&&(
            <div style={{display:"flex",alignItems:"center",gap:6,
              background:`${cfg.accent}10`,border:`1px solid ${cfg.accent}30`,
              borderRadius:8,padding:"4px 12px"}}>
              <span style={{fontSize:11,color:cfg.accent,fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}>
                ✅ {fromDt.replace("T"," ")} → {toDt.replace("T"," ")}
              </span>
            </div>
          )}
        </div>

        <div style={{width:1,height:20,background:theme.border}}/>

        {/* Tab selector */}
        {[["server","🖥 Server Metrics"],["service","⚙ Service Metrics"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{
            fontSize:12,padding:"5px 14px",borderRadius:6,cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontWeight:600,
            background:tab===k?`${cfg.accent}18`:theme.inputBg,
            border:`1px solid ${tab===k?`${cfg.accent}50`:theme.border}`,
            color:tab===k?cfg.accent:theme.textMuted,transition:"all .15s",
          }}>{l}</button>
        ))}

        {loading&&(
          <div style={{width:18,height:18,borderRadius:"50%",marginLeft:4,
            border:`2px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
            animation:"spin .7s linear infinite"}}/>
        )}
      </div>

      {/* Content */}
      <div style={{flex:1,overflowY:"auto",padding:"20px",minHeight:0,paddingBottom:40}}>

        {tab==="server"&&(
          <div id="bn360-charts-server" style={{display:"flex",flexDirection:"column",gap:20}}>
            {/* CPU Chart */}
            <div style={{background:theme.bgCard,borderRadius:14,padding:"20px",
              border:`1px solid ${theme.border}`}}>
              <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                letterSpacing:".1em",marginBottom:16}}>⚡ CPU USAGE %</div>
              {svrData.length===0?(
                <div style={{height:200,display:"flex",alignItems:"center",justifyContent:"center",
                  color:theme.textMuted,fontSize:13}}>
                  {loading?"Loading...":"No data for this range"}
                </div>
              ):(
                <ResponsiveContainer width="100%" height={200}>
                  {chartType==="bar" ? (
                  <BarChart data={svrData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                    <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:10,fill:axisColor}} tickLine={false}/>
                    <YAxis domain={[0,100]} tick={{fontSize:10,fill:axisColor}} tickLine={false} axisLine={false} unit="%"/>
                    <Tooltip content={<CustomTooltip theme={theme}/>}/>
                    <Bar dataKey="cpu" name="CPU" fill={cfg.accent} radius={[2,2,0,0]}/>
                  </BarChart>
                  ) : (
                  <AreaChart data={svrData}>
                    <defs>
                      <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={cfg.accent} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={cfg.accent} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                    <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:10,fill:axisColor}} tickLine={false}/>
                    <YAxis domain={[0,100]} tick={{fontSize:10,fill:axisColor}} tickLine={false} axisLine={false} unit="%"/>
                    <Tooltip content={<CustomTooltip theme={theme}/>}/>
                    <Area type="monotone" dataKey="cpu" name="CPU" stroke={cfg.accent}
                      fill="url(#cpuGrad)" strokeWidth={2} dot={false}/>
                  </AreaChart>
                  )}
                </ResponsiveContainer>
              )}
            </div>

            {/* RAM Chart */}
            <div style={{background:theme.bgCard,borderRadius:14,padding:"20px",
              border:`1px solid ${theme.border}`}}>
              <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                letterSpacing:".1em",marginBottom:16}}>💾 RAM USAGE %</div>
              {svrData.length>0&&(
                <ResponsiveContainer width="100%" height={200}>
                  {chartType==="bar" ? (
                  <BarChart data={svrData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                    <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:10,fill:axisColor}} tickLine={false}/>
                    <YAxis domain={[0,100]} tick={{fontSize:10,fill:axisColor}} tickLine={false} axisLine={false} unit="%"/>
                    <Tooltip content={<CustomTooltip theme={theme}/>}/>
                    <Bar dataKey="ram" name="RAM" fill="#10b981" radius={[2,2,0,0]}/>
                  </BarChart>
                  ) : (
                  <AreaChart data={svrData}>
                    <defs>
                      <linearGradient id="ramGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                    <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:10,fill:axisColor}} tickLine={false}/>
                    <YAxis domain={[0,100]} tick={{fontSize:10,fill:axisColor}} tickLine={false} axisLine={false} unit="%"/>
                    <Tooltip content={<CustomTooltip theme={theme}/>}/>
                    <Area type="monotone" dataKey="ram" name="RAM" stroke="#10b981"
                      fill="url(#ramGrad)" strokeWidth={2} dot={false}/>
                  </AreaChart>
                  )}
                </ResponsiveContainer>
              )}
            </div>

            {/* Disk + Load */}
            <div style={{display:"flex",flexDirection:"column",gap:20,width:"100%"}}>
              <div style={{background:theme.bgCard,borderRadius:14,padding:"20px",
                border:`1px solid ${theme.border}`}}>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:16}}>💿 DISK USAGE %</div>
                {svrData.length>0&&(
                  <ResponsiveContainer width="100%" height={200}>
                    {chartType==="bar" ? (
                    <BarChart data={svrData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                      <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:9,fill:axisColor}} tickLine={false}/>
                      <YAxis domain={[0,100]} tick={{fontSize:9,fill:axisColor}} tickLine={false} axisLine={false} unit="%"/>
                      <Tooltip content={<CustomTooltip theme={theme}/>}/>
                      <Bar dataKey="disk_root" name="Disk" fill="#f59e0b" radius={[2,2,0,0]}/>
                    </BarChart>
                    ) : (
                    <AreaChart data={svrData}>
                      <defs>
                        <linearGradient id="diskGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                      <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:9,fill:axisColor}} tickLine={false}/>
                      <YAxis domain={[0,100]} tick={{fontSize:9,fill:axisColor}} tickLine={false} axisLine={false} unit="%"/>
                      <Tooltip content={<CustomTooltip theme={theme}/>}/>
                      <Area type="monotone" dataKey="disk_root" name="Disk" stroke="#f59e0b"
                        fill="url(#diskGrad)" strokeWidth={2} dot={false}/>
                    </AreaChart>
                    )}
                  </ResponsiveContainer>
                )}
              </div>
              <div style={{background:theme.bgCard,borderRadius:14,padding:"20px",
                border:`1px solid ${theme.border}`}}>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:16}}>📊 LOAD AVERAGE</div>
                {svrData.length>0&&(
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={svrData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                      <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:9,fill:axisColor}} tickLine={false}/>
                      <YAxis tick={{fontSize:9,fill:axisColor}} tickLine={false} axisLine={false}/>
                      <Tooltip content={<CustomTooltip theme={theme} />}/>
                      <Line type="monotone" dataKey="load1" name="1m" stroke="#ef4444" strokeWidth={1.5} dot={false}/>
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        )}

        {tab==="service"&&(
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            {/* Service selector */}
            <div style={{background:theme.bgCard,borderRadius:14,padding:"16px 20px",
              border:`1px solid ${theme.border}`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <span style={{fontSize:12,color:theme.textMuted,fontWeight:600}}>SERVICE:</span>
              <select value={selSvc} onChange={e=>{setSelSvc(e.target.value);}}
                style={{background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.text,borderRadius:6,padding:"6px 12px",
                  fontFamily:"'Inter',sans-serif",fontSize:13,outline:"none",
                  cursor:"pointer",minWidth:220}}>
                {svcList.map(s=>(<option key={s} value={s}>{s}</option>))}
              </select>
              <button onClick={loadSvcData} style={{
                background:`${cfg.accent}18`,border:`1px solid ${cfg.accent}50`,
                color:cfg.accent,borderRadius:6,padding:"6px 14px",cursor:"pointer",
                fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600}}>
                Load
              </button>
            </div>

            {svcData.length>0&&(
              <>
                {/* CPU trend */}
                <div style={{background:theme.bgCard,borderRadius:14,padding:"20px",
                  border:`1px solid ${theme.border}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                    letterSpacing:".1em",marginBottom:16}}>⚡ {selSvc} — CPU %</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={svcData}>
                      <defs>
                        <linearGradient id="scpuGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={cfg.accent} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={cfg.accent} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                      <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:10,fill:axisColor}} tickLine={false}/>
                      <YAxis tick={{fontSize:10,fill:axisColor}} tickLine={false} axisLine={false} unit="%"/>
                      <Tooltip content={<CustomTooltip theme={theme}/>}/>
                      <Area type="monotone" dataKey="cpu" name="CPU" stroke={cfg.accent}
                        fill="url(#scpuGrad)" strokeWidth={2} dot={false}/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Memory trend */}
                <div style={{background:theme.bgCard,borderRadius:14,padding:"20px",
                  border:`1px solid ${theme.border}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                    letterSpacing:".1em",marginBottom:16}}>💾 {selSvc} — Memory (MB)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={svcData}>
                      <defs>
                        <linearGradient id="smemGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor}/>
                      <XAxis dataKey="ts" tickFormatter={smartFmt} tick={{fontSize:10,fill:axisColor}} tickLine={false}/>
                      <YAxis tick={{fontSize:10,fill:axisColor}} tickLine={false} axisLine={false} unit=" MB"/>
                      <Tooltip content={<CustomTooltip theme={theme}/>}/>
                      <Area type="monotone" dataKey="memory_mb" name="Memory" stroke="#10b981"
                        fill="url(#smemGrad)" strokeWidth={2} dot={false}/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Status timeline */}
                <div style={{background:theme.bgCard,borderRadius:14,padding:"20px",
                  border:`1px solid ${theme.border}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                    letterSpacing:".1em",marginBottom:16}}>🟢 STATUS TIMELINE</div>
                  <div style={{display:"flex",gap:2,flexWrap:"wrap"}}>
                    {svcData.map((d,i)=>(
                      <div key={i} title={`${fmtDate(d.ts)}: ${d.status}`}
                        style={{width:8,height:24,borderRadius:2,flexShrink:0,
                          background:d.status==="running"||d.status==="active"?"#10b981":
                            d.status==="failed"?"#ef4444":"#475569",
                          transition:"opacity .2s",cursor:"default",
                        }}/>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:16,marginTop:10}}>
                    {[["#10b981","Running"],["#475569","Stopped"],["#ef4444","Failed"]].map(([c,l])=>(
                      <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                        <div style={{width:10,height:10,borderRadius:2,background:c}}/>
                        <span style={{fontSize:11,color:theme.textMuted}}>{l}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {svcData.length===0&&!loading&&(
              <div style={{background:theme.bgCard,borderRadius:14,padding:"40px",
                border:`1px solid ${theme.border}`,textAlign:"center",
                color:theme.textMuted,fontSize:13}}>
                Select a service and click Load to view history
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
